import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import { IPrefillCartMutationResponse } from '../../graphQL/mutations/prefillCart/types'
import { IOrderLine } from '../../graphQL/queries/getOrders/types'
import { ICartProduct } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import {
  IAddPrefillCartItem,
  useAddPrefillCart,
} from '../prefillCart/useAddPrefillCart'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import type { IReorderParams } from '../../interfaces/tag_manager'

const FUNCTION_NAME = 'useReorder'

/**
 * Why a reorder returned before reaching the mutation. A Datadog facet, so a
 * monitor can split an authoring slip (`noOrderId`) from a screen that drew
 * the CTA over an order this hook cannot see (`notLoaded`).
 */
type ReorderNoopReason = 'noOrderId' | 'notLoaded' | 'noLines' | 'noSession'

interface ReorderArgs {
  orderId?: unknown
  value?: unknown
}

const readOrderId = (args: ReorderArgs): string => {
  const candidates = [args.orderId, args.value]
  const found = candidates.find(
    (candidate) => typeof candidate === 'string' && candidate.length > 0,
  )
  return typeof found === 'string' ? found : ''
}

/**
 * Maps an order line onto the shape `useAddPrefillCart` consumes.
 *
 * The hook reads `item.package?.name` for `selectedPackage` and uses `price`
 * and `name` for the GTM event only, so the null coercions here cost nothing
 * but a less precise analytics payload on an order that came back without
 * them.
 * @internal
 */
export const toPrefillCartItems = (
  lines: IOrderLine[],
): IAddPrefillCartItem[] =>
  lines.map((line) => ({
    sku: line.sku,
    quantity: line.quantity,
    price: line.price ?? 0,
    name: line.name ?? '',
    discounts: 0,
    package: line.package?.name
      ? ({ name: line.package.name } as ICartProduct['package'])
      : null,
  }))

/**
 * "Order again", from an Order History row or from Order Detail.
 *
 * The order is looked up in the history `list` first and in the detail
 * slice's `selected` second (COM-860). Order Detail fills `selected` and
 * never touches `list`, so reading `list` alone missed every deep link, every
 * order a status filter had removed and every order on a page not yet
 * loaded. `list` first keeps the History path exactly as it was. Both sources
 * carry `package { name }`, which is what keeps the shopper's packaging on a
 * reorder from either screen.
 *
 * Deliberately not a per-item loop over the cart hook. `useAddPrefillCart`
 * already adds a whole list in one `prefillCart` mutation and already owns
 * `onCartResponse`, `setSynchronizingCart`, the GTM event and the DataDog
 * context, so reorder is a call to it.
 *
 * Scoped to the session the shopper is in right now. The mutation carries the
 * `sessionUid` currently in `defaultSlice` and nothing here reads or writes
 * any other session — reorder across a session hop is REQ-02 in Scoping M3,
 * after this.
 */
export const useReorder = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // Read through a ref so the handler reads the location as of the tap, not
  // as of the render that registered it — EventManager re-registers on store
  // changes, not on navigation. Same pattern as `useOrderActions`.
  const location = useLocation()
  const locationRef = useRef(location)
  locationRef.current = location
  const { addPrefillCart } = useAddPrefillCart()
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionUid = useAppSelector((state) => state.defaultSlice.sessionId)
  const texts = useAppSelector((state) => state.defaultSlice.config?.texts)
  const replaceCart = useAppSelector(
    (state) => state.defaultSlice.config?.options?.reorderReplacesCart ?? false,
  )
  const list = useAppSelector((state) => state.ordersSlice.list)
  const selected = useAppSelector((state) => state.ordersSlice.selected)

  /**
   * The lines of the order to reorder, or `undefined` when neither source
   * holds it. An order found with no lines comes back as `[]`, so the caller
   * can tell the two apart.
   */
  const findOrderLines = (orderId: string): IOrderLine[] | undefined => {
    const fromList = list.find((candidate) => candidate.id === orderId)
    if (fromList) return fromList.items || []
    if (selected?.id === orderId) return selected.items || []
    return undefined
  }

  /**
   * A reorder that cannot run, reported rather than returned silently
   * (COM-860). The panel drawing the CTA has every reason to think the
   * reorder is valid, so the shopper taps a live-looking control and the
   * screen does not move; before this, that left no trace anywhere. The same
   * shape as `returnToConversation`'s no-phone report, `startView` included,
   * because no navigation follows to mark the moment.
   *
   * Telemetry only, no alert, as that report does. One tap is one report.
   */
  const reportNoop = (reason: ReorderNoopReason, orderId: string) => {
    const context = generateDataDogContext({
      title: 'reorder did nothing',
      extraInfo: {
        function: FUNCTION_NAME,
        reason,
        orderId,
        listSize: list.length,
        hasSelected: !!selected,
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(new Error(`reorder did nothing: ${reason}`), context)
  }

  /**
   * Warns when the backend dropped lines that are no longer purchasable.
   *
   * `prefillCart` returns the resulting cart, never a rejection list, so a
   * dropped line is simply one that never arrives. The shopper still goes to
   * the cart: what did get added is really there, and an alert they can read
   * beats a silent partial reorder.
   */
  const alertOnDroppedItems = (
    requested: IAddPrefillCartItem[],
    data: IPrefillCartMutationResponse,
  ) => {
    const returnedSkus = new Set(
      (data?.prefillCart?.items || []).map((item) => item.sku),
    )
    const dropped = requested.filter((item) => !returnedSkus.has(item.sku))
    if (!dropped.length) return

    const message = getAlertErrorMessage(
      AlertMessageType.ReorderPartial,
      texts,
      dropped.length,
    )
    if (message) dispatch(addAlert({ message }))

    const context = generateDataDogContext({
      title: 'reorder dropped unavailable items',
      extraInfo: {
        function: FUNCTION_NAME,
        requested: requested.length,
        droppedSkus: dropped.map((item) => item.sku),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addAction('reorder_items_dropped', context)
  }

  /**
   * Nothing reached the cart, so `/cart` would be a lie. Stay put and say so.
   */
  const failReorder = (exception: unknown, orderId: string) => {
    const message = getAlertErrorMessage(AlertMessageType.ReorderFailed, texts)
    if (message) dispatch(addAlert({ message }))

    const context = generateDataDogContext({
      title: 'reorder failed',
      extraInfo: { function: FUNCTION_NAME, orderId },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      exception instanceof Error ? exception : new Error('reorder failed'),
      context,
    )
  }

  /**
   * GA4 `commerce-reorder`: fired once per call, carrying what happened.
   *
   * `reorder_source` reads the **initiating surface** off `useLocation()` at
   * invocation. Attribution by cache membership (`list.some(id)` vs
   * `selected?.id === id`) was wrong: opening a detail from History leaves
   * `list` populated AND loads `selected`, so Detail reorders always found
   * the order in `list` and reported `order_history`. The route the shopper
   * tapped on is the honest answer.
   *
   * Pathname shape: `/${slug}/${sessionId}/orders` is History and
   * `/${slug}/${sessionId}/orders/${orderId}` is Detail. The two-segment
   * tail `/orders` is History and anything deeper is Detail. Fallback to
   * `order_history` for a reorder taken from elsewhere entirely (deep link,
   * authored embed), so the event always carries a facet.
   */
  const fireReorder = (
    orderId: string,
    result: IReorderParams['reorder_result'],
    itemCount: number,
    value: number,
  ) => {
    const path = locationRef.current.pathname ?? ''
    const source: IReorderParams['reorder_source'] = /\/orders\/[^/]+/.test(
      path,
    )
      ? 'order_detail'
      : 'order_history'
    fireGTMEvent('commerce-reorder', {
      reorder_source: source,
      reorder_result: result,
      item_count: itemCount,
      value,
    })
  }

  const reorderItems = async (args: ReorderArgs = {}) => {
    const orderId = readOrderId(args)
    if (!orderId) {
      reportNoop('noOrderId', orderId)
      fireReorder(orderId, 'failed', 0, 0)
      return
    }

    const lines = findOrderLines(orderId)
    if (!lines) {
      reportNoop('notLoaded', orderId)
      fireReorder(orderId, 'failed', 0, 0)
      return
    }

    // Sending an empty item list would still hit the API and, with
    // replaceCart on, would empty the cart for nothing.
    if (!lines.length) {
      reportNoop('noLines', orderId)
      fireReorder(orderId, 'failed', 0, 0)
      return
    }

    if (!storefrontName || !sessionUid) {
      reportNoop('noSession', orderId)
      fireReorder(orderId, 'failed', 0, 0)
      return
    }

    const items = toPrefillCartItems(lines)
    const itemValue = items.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0,
    )

    let added: IPrefillCartMutationResponse | null = null
    try {
      added = await addPrefillCart({
        items,
        storefrontName,
        sessionUid,
        replaceCart,
        referrer: Referrers.ORDER_HISTORY_REORDER,
        onCompleted: (data) => {
          const returnedSkus = new Set(
            (data?.prefillCart?.items || []).map((item) => item.sku),
          )
          const partial = items.some((item) => !returnedSkus.has(item.sku))
          alertOnDroppedItems(items, data)
          fireReorder(
            orderId,
            partial ? 'partial' : 'added',
            items.length,
            itemValue,
          )
        },
      })
    } catch (exception) {
      // Reachable only for a synchronous throw before the mutation — the GTM
      // event and the reduce over item prices both run first.
      failReorder(exception, orderId)
      fireReorder(orderId, 'failed', items.length, itemValue)
      return
    }

    // A failed mutation resolves rather than rejecting, because
    // `useAddPrefillCart` registers an `onError` callback and Apollo returns
    // `{ data: undefined, errors }` in that case instead of throwing. So the
    // null return, not the catch above, is what a network or GraphQL failure
    // actually looks like here.
    if (!added) {
      failReorder(null, orderId)
      fireReorder(orderId, 'failed', items.length, itemValue)
      return
    }

    navigate(`/${storefrontName}/${sessionUid}/cart`)
  }

  return { reorderItems }
}

export default useReorder
